import type { BallColour, Shot } from '@shootout-comps/types';

export const ALL_BALLS: BallColour[] = ['red', 'yellow', 'green', 'brown', 'blue', 'pink', 'black'];

export const BALL_CSS: Record<BallColour, string> = {
  red: '#c0392b', yellow: '#f1c40f', green: '#27ae60', brown: '#795548',
  blue: '#2980b9', pink: '#e91e8c', black: '#1a1a1a', white: '#f5f0e8',
};

/**
 * Determines which balls are legally pottable on the next shot.
 *
 * Snooker sequence while reds remain:
 *   red → colour → red → colour → ...
 *
 * Key insight: only SUCCESSFUL POTS advance the sequence.
 * Misses, fouls and no-pots do NOT change what ball is expected next.
 * Count the number of successful pots so far:
 *   even count (0,2,4,...) → next must be a RED
 *   odd  count (1,3,5,...) → next must be any COLOUR
 *
 * Once all reds are gone:
 *   colours must be potted in ascending order: yellow→green→brown→blue→pink→black
 *
 * `freeBallAvailable` widens a single-ball-constrained result (red-only, or one specific
 * colour during the colours-only endgame) to every colour — mirrors game-logic.ts's
 * getLegalBallsServer (kept in sync manually; the server is the authoritative enforcer, this
 * is only used to decide which balls the scoring UI shows as tappable).
 */
export function getLegalBalls(shots: Shot[], redsRemaining: number, freeBallAvailable?: boolean): Set<BallColour> {
  const COLOUR_ORDER: BallColour[] = ['yellow', 'green', 'brown', 'blue', 'pink', 'black'];
  const COLOURS = new Set<BallColour>(COLOUR_ORDER);
  const widen = (strict: Set<BallColour>): Set<BallColour> =>
    (freeBallAvailable && strict.size === 1) ? new Set<BallColour>([...strict, ...COLOUR_ORDER]) : strict;

  if (redsRemaining === 0) {
    // Walk back through successful pots to find where we are in the colours phase.
    // Stop when we hit a red — everything after that red is the colours sequence so far.
    // Genuine free-ball pots don't advance this sequence (the real ball on stays "on").
    const allPots = shots.filter(s => !s.isFoul && !!s.ballPotted && !s.isFreeBall);

    // Find the index of the last red pot in the successful pots list
    let lastRedIdx = -1;
    for (let i = allPots.length - 1; i >= 0; i--) {
      if (allPots[i].ballPotted === 'red') { lastRedIdx = i; break; }
    }

    if (lastRedIdx === -1) {
      // No reds at all in history — must be colours-only from the start.
      // Count how many colours have been potted since game began.
      const coloursPotted = allPots.filter(p => COLOURS.has(p.ballPotted as BallColour)).length;
      if (coloursPotted >= COLOUR_ORDER.length) return new Set();
      return widen(new Set<BallColour>([COLOUR_ORDER[coloursPotted]]));
    }

    // Colours potted after the last red
    const afterLastRed = allPots.slice(lastRedIdx + 1);
    const coloursAfterLastRed = afterLastRed.filter(p => COLOURS.has(p.ballPotted as BallColour));

    if (coloursAfterLastRed.length === 0) {
      // The 15th red was just potted, no colour has followed yet —
      // player may pot ANY colour (free colour, same as any red→colour in a break)
      return new Set<BallColour>(COLOUR_ORDER);
    }

    // The first colour after the last red is the "free colour" (goes back on table).
    // The ordered sequence (yellow→green→...→black) starts AFTER that free colour.
    // So subtract 1: seqCount=0 means next is yellow, seqCount=1 means next is green, etc.
    const seqCount = coloursAfterLastRed.length - 1;
    if (seqCount >= COLOUR_ORDER.length) return new Set();
    return widen(new Set<BallColour>([COLOUR_ORDER[seqCount]]));
  }

  // Reds remain — walk backwards through shots to find the current player's unbroken pot streak.
  // Stop on: miss, foul, or a shot by a different player (turn ended = sequence resets).
  const currentPlayer = shots.length > 0 ? shots[shots.length - 1].playerId : '';
  let currentTurnPots = 0;
  for (let i = shots.length - 1; i >= 0; i--) {
    const s = shots[i];
    if (s.isFoul || !s.ballPotted || s.playerId !== currentPlayer) {
      break;
    }
    currentTurnPots++;
  }

  // Even count in current turn → need a red; odd count → need a colour
  if (currentTurnPots % 2 === 0) {
    return widen(new Set<BallColour>(['red']));
  } else {
    return new Set<BallColour>(COLOUR_ORDER);
  }
}

export function fmt(s: number): string {
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

export function lighten(hex: string): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, ((n >> 16) & 0xff) + 60);
  const g = Math.min(255, ((n >> 8) & 0xff) + 60);
  const b = Math.min(255, (n & 0xff) + 60);
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

// Current unbroken break for a player — walk backwards through shots
export function computeBreak(shots: Shot[], playerId: string): number {
  let total = 0;
  for (let i = shots.length - 1; i >= 0; i--) {
    const s = shots[i];
    if (s.isFoul || !s.ballPotted || s.playerId !== playerId) break;
    total += Number(s.points) > 0 ? Number(s.points) : 0;
  }
  return total;
}

// Highest break a player has made so far in the match — walk forward through the whole shot
// history, summing consecutive pots (mirrors the server's tournament-wide extractBreaks in
// apps/api/src/routes/match.routes.ts, scoped to one player and reduced to a single max).
export function computeHighBreak(shots: Shot[], playerId: string): number {
  let max = 0;
  let i = 0;
  while (i < shots.length) {
    const s = shots[i];
    const isPot = !s.isFoul && !!s.ballPotted && Number(s.points) > 0;
    if (isPot) {
      const pid = s.playerId;
      let total = 0;
      while (i < shots.length && !shots[i].isFoul && !!shots[i].ballPotted && Number(shots[i].points) > 0 && shots[i].playerId === pid) {
        total += Number(shots[i].points); i++;
      }
      if (pid === playerId && total > max) max = total;
    } else {
      i++;
    }
  }
  return max;
}

// Average time a player takes per shot (seconds), across every shot they've played so far —
// pots, misses and fouls all count, since each one still took time at the table. Recomputed
// fresh from `shots` on every call (same lightweight style as computeBreak/computeHighBreak)
// rather than tracked as running state, so it works identically live or from archived history.
export function computeAvgShotTime(shots: Shot[], playerId: string): number {
  const playerShots = shots.filter(s => s.playerId === playerId);
  if (playerShots.length === 0) return 0;
  const total = playerShots.reduce((sum, s) => sum + (Number(s.shotDurationSeconds) || 0), 0);
  return total / playerShots.length;
}
